import { Divider, Paper, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { ExtraSmallButton } from "../../custom";
import { useSelector } from "react-redux";
import { selectTradeOrders } from "../../../selectors/strategyBuilder";
import useSelectPrimaryOrUnderlyingSecurity from "../../../hooks/useSelectPrimaryOrUnderlyingSecurity";
import { selectOptionsTradeData, selectUnderlyingOptionsTradeData } from "../../../selectors/options";
import { OptionType, Security, SecurityCategory_CategoryCode } from "../../../proto/generated/security";
import { useDispatch } from "react-redux";
import { TradeOrder, updateTradeOrders } from "../../../reducer/strategyBuilder.reducer";
import StrikesDownIcon from "../../../assets/icons/StrikesDownIcon";
import StrikesUpIcon from "../../../assets/icons/StrikesUpIcon";
import { selectOrdersHistory } from "../../../selectors/orders";
import { OrderStatus } from "../../../proto/generated/order";

const StrikeControls: React.FC<{disabled?: boolean}> = ({disabled}) => {

    const dispatch = useDispatch();
    const { primaryOrUnderlyingToken: underlyingToken } = useSelectPrimaryOrUnderlyingSecurity();
    const tradeOrders = useSelector(selectTradeOrders);
    const optionsTradeData = useSelector(selectOptionsTradeData);
    const optionsTradeDataRef = useRef(optionsTradeData ? optionsTradeData[underlyingToken] : null);

    const orderHistory = useSelector(selectOrdersHistory) || [];
    
    const orderHistoryMap = useMemo(() => orderHistory.reduce((map, order) => {
        map[order.orderId] = order;
        return map;
    }, {} as Record<string, typeof orderHistory[0]>), [orderHistory]);

    useEffect(() => {
        optionsTradeDataRef.current = optionsTradeData ? optionsTradeData[underlyingToken] : null;
    }, [optionsTradeData])
    
    const optionExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))

    const [decreaseEnabled, increaseEnabled] = useMemo(() => {

        if (!optionsTradeDataRef.current ) return [false, false];
        let previousEnabled = true, nextEnabled = true;

        tradeOrders.forEach((t) => {
            const {security} = t;
            if (security.categoryCode === SecurityCategory_CategoryCode.FUTURES) {
                return;
            } else {

                const expiryData = optionsTradeDataRef.current.find(t => t.expiration.expiry.expiry === security.expiry.expiry);

                if (!expiryData || !expiryData.trades?.length || !expiryData.strikes?.length) {
                    previousEnabled = false;
                    nextEnabled = false;
                    return;
                }

                const sortedStrikes = [...expiryData.strikes].sort((a, b) => a.strike - b.strike)
                const strikeIndex = sortedStrikes.findIndex(s => s.strike === security.strikePrice);
                if (strikeIndex === 0) previousEnabled = false;
                if (strikeIndex === sortedStrikes.length - 1) nextEnabled = false;
            }
        })
        return [previousEnabled, nextEnabled];
    }, [tradeOrders])


    const onStrikeChange = useCallback(async (direction: number) => {
        if (!optionsTradeDataRef.current) return;
        
        const newTradeOrders: TradeOrder[] = tradeOrders.map((t) => {
            const {security} = t;

            if (orderHistoryMap[t.referenceOrderId]?.status === OrderStatus.EXECUTED) return t;

            let newSecurity: Security = null;
            let price = 0;

            if (security.categoryCode === SecurityCategory_CategoryCode.OPTIONS) {
                const expiryData = optionsTradeDataRef.current.find(t => t.expiration.expiry.expiry === security.expiry.expiry);
        
                const sortedStrikes = [...expiryData.strikes].sort((a, b) => a.strike - b.strike)
                
                if (!sortedStrikes.length) return t;
                const index = sortedStrikes.findIndex(s => s.strike === security.strikePrice)
                const newStrike = sortedStrikes[index + direction]?.strike;
                if (!newStrike) return t;
                const newTradeData = expiryData.trades?.find(t => t.strikeCol.strike === newStrike);
                newSecurity = newTradeData[security.optionType === OptionType.CE ? "callOption" : "putOption"].security;
                price = newTradeData[security.optionType === OptionType.CE ? "callOption" : "putOption"].ltp
            } else if (security.categoryCode === SecurityCategory_CategoryCode.FUTURES) {
                return t;
            }

            if (!newSecurity) return t;
            return {
                ...t,
                security: newSecurity,
                price
            }
        });

        dispatch(updateTradeOrders(newTradeOrders));
        
    }, [underlyingToken, tradeOrders, optionExpiries, orderHistoryMap]);
    
    const onIncreaseStrike = useCallback(() => {
        onStrikeChange(1);
    }, [onStrikeChange]);

    const onDecreaseStrike = useCallback(() => {
        onStrikeChange(-1);
    }, [onStrikeChange])

    return  <>
                <Typography variant="caption" color={'text.secondary'}
                            style={{ margin: '5px 0' }}>STRIKES</Typography>
                <Paper className="flex !rounded-sm relative items-center"
                    sx={{ background: (theme) => theme.palette.background.default }}>
                    <ExtraSmallButton data-event={'Strike Down Clicked'} data-event-source={'Trade'}
                        style={{ width: '50px', height: '100%' }}
                        onClick={onDecreaseStrike}
                        disabled={!decreaseEnabled || disabled}
                        className="button-icons"
                        color={'inherit'}
                        size={'small'}><StrikesDownIcon /></ExtraSmallButton>
                    <Divider orientation="vertical" variant="middle" flexItem />
                    <ExtraSmallButton data-event={'Strike Up Clicked'} data-event-source={'Trade'}
                        style={{ width: '50px', height: '100%' }}
                        onClick={onIncreaseStrike}
                        disabled={!increaseEnabled || disabled}
                        className="button-icons"
                        color={'inherit'}
                        size={'small'}><StrikesUpIcon /></ExtraSmallButton>
                </Paper>
            </>
};


export default memo(StrikeControls);